<template>
  <div v-if="details.length">
    <div
      v-if="nationalityDetails.length"
      class="group"
    >
      <div class="group--title">
        {{ $t('kyc.customer_nationality') }}
      </div>
      <div
        v-for="(data, i) in nationalityDetails"
        :key="i"
        class="group--fields"
      >
        <div class="group--topic">
          {{ i +1 }}. {{ data.topic | displayFromKeyToText(kycTopicsList[customerType][data.group], $i18n.locale) }}
        </div>
        <div class="group--detail"> {{ data.detail }}</div>
        <Nationality
          :form.sync="form"
          :countries="countries"
          :data="data"
          class="group--field"
        />
      </div>
    </div>

    <div
      v-if="informationDetails.length"
      class="group"
    >
      <div class="group--title">
        {{ customerType === 'Personal' ? $t('kyc.customer_information') : $t('kyc.org_info') }}
      </div>
      <div
        v-for="(data, i) in informationDetails"
        :key="i"
        class="group--fields"
      >
        <div class="group--topic">
          {{ i +1 }}. {{ data.topic | displayFromKeyToText(kycTopicsList[customerType][data.group], $i18n.locale) }}
        </div>
        <div class="group--detail"> {{ data.detail }}</div>
        <CustomerInformation
          v-if="customerType === 'Personal'"
          :form.sync="form"
          :data="data"
          :working-at-company-country="workingAtCompanyCountry"
          :countries="countries"
          :provinces-in-thailand="provincesInThailand"
          :occupation-list="occupationList"
          :average-income-list="averageIncomeList"
          class="group--field"
        />
        <OrgInformation
          v-else
          :form.sync="form"
          :data="data"
          :org-country="orgCountry"
          :countries="countries"
          :provinces-in-thailand="provincesInThailand"
          :business-list="businessList"
          :average-income-list="averageIncomeList"
          class="group--field"
        />
      </div>
    </div>

    <div
      v-if="authorizedRepresentativeDetails.length"
      class="group"
    >
      <div class="group--title">
        {{ $t('kyc.auth_represent') }}
      </div>
      <div
        v-for="(data, i) in authorizedRepresentativeDetails"
        :key="i"
        class="group--fields"
      >
        <div class="group--topic">
          {{ i +1 }}. {{ data.topic | displayFromKeyToText(kycTopicsList[customerType][data.group], $i18n.locale) }}
        </div>
        <div class="group--detail"> {{ data.detail }}</div>
        <AuthorizedRepresentative
          :form.sync="form"
          :data="data"
          class="group--field"
        />
      </div>
    </div>

    <div
      v-if="addressDetails.length"
      class="group"
    >
      <div class="group--title">
        {{ customerType === 'Personal' ? $t('kyc.customer_address') : $t('kyc.auth_represent_addr') }}
      </div>
      <div
        v-for="(data, i) in addressDetails"
        :key="i"
        class="group--fields"
      >
        <div class="group--topic">
          {{ i +1 }}. {{ data.topic | displayFromKeyToText(kycTopicsList[customerType][data.group], $i18n.locale) }}
        </div>
        <div class="group--detail"> {{ data.detail }}</div>
        <Address
          :form.sync="form"
          :countries="countries"
          :provinces-in-thailand="provincesInThailand"
          :origin-country="originCountry"
          :current-country="currentCountry"
          :data="data"
          class="group--field"
        />
      </div>
    </div>

    <div
      v-if="identityDetails.length"
      class="group"
    >
      <div class="group--title">
        {{ customerType === 'Personal' ? $t('kyc.customer_identification') : $t('kyc.auth_represent_identity') }}
      </div>
      <div
        v-for="(data, i) in identityDetails"
        :key="i"
        class="group--fields"
      >
        <div
          v-if="data.topic === 'identificationId'"
          class="group--topic"
        >
          {{ i + 1 }}. {{ (form.nationality && form.nationality === 'Thai') || (nationality === 'Thai' && !form.hasOwnProperty('nationality')) ? $t('kyc.id_card') : $t('kyc.passport') }}
        </div>
        <div
          v-else
          class="group--topic"
        >
          {{ i + 1 }}. {{ data.topic | displayFromKeyToText(kycTopicsList[customerType][data.group], $i18n.locale) }}
        </div>
        <div class="group--detail"> {{ data.detail }}</div>
        <Identity
          :form.sync="form"
          :data="data"
          :nationality="nationality"
          :type-of-card="typeOfCard"
          class="group--field"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <div
      v-for="(index) in 3"
      :key="index"
      class="group"
    >
      <b-skeleton
        width="300px"
        height="25px"
      />
      <b-skeleton
        width="70px"
        height="25px"
      />
      <b-skeleton
        width="150px"
        height="25px"
      />
    </div>
  </div>
</template>

<script>
import Nationality from './Nationality'
import CustomerInformation from './CustomerInformation'
import OrgInformation from './OrgInformation'
import AuthorizedRepresentative from './AuthorizedRepresentative'
import Address from './Address'
import Identity from './Identify'

export default {
  name: 'EditKYCForm',
  inject: ['$validator'],
  components: {
    Nationality,
    CustomerInformation,
    OrgInformation,
    AuthorizedRepresentative,
    Address,
    Identity
  },
  props: {
    form: {
      type: Object,
      default: () => {}
    },
    countries: {
      type: Array,
      default: () => []
    },
    provincesInThailand: {
      type: Array,
      default: () => []
    },
    occupationList: {
      type: Array,
      default: () => []
    },
    businessList: {
      type: Array,
      default: () => []
    },
    averageIncomeList: {
      type: Array,
      default: () => []
    },
    customerType: {
      type: String, // Personal || Organization
      required: true
    },
    workingAtCompanyCountry: {
      type: String, // Personal
      required: true
    },
    originCountry: {
      type: String,
      required: true
    },
    currentCountry: {
      type: String,
      required: true
    },
    orgCountry: {
      type: String,
      required: true
    },
    details: {
      type: Array,
      default: () => []
    },
    nationality: {
      type: String,
      required: true
    },
    typeOfCard: {
      type: String,
      required: true
    },
    kycTopicsList: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    nationalityDetails () {
      if (this.details.length) {
        return this.details.filter(d => d.group === 'Nationality')
      }
      return []
    },
    informationDetails () {
      if (this.details.length) {
        return this.details.filter(d => d.group === 'Information')
      }
      return []
    },
    authorizedRepresentativeDetails () {
      if (this.details.length) {
        return this.details.filter(d => d.group === 'Authorized Representative')
      }
      return []
    },
    addressDetails () {
      if (this.details.length) {
        if (this.customerType === 'Personal') {
          return this.details.filter(d => d.group === 'Address')
        }
        return this.details.filter(d => d.group === 'Authorized Representative Address')
      }
      return []
    },
    identityDetails () {
      if (this.details.length) {
        if (this.customerType === 'Personal') {
          return this.details.filter(d => d.group === 'Identity')
        }
        return this.details.filter(d => d.group === 'Authorized Representative Identity')
      }
      return []
    }
  }
}
</script>

<style lang="scss">
.group {
  margin-top: 60px;
  &--title {
    font-family: Roboto;
    font-size: 25px;
    font-weight: bold;
    color: #000000;
  }
  &--fields {
    margin-top: 30px;
  }
  &--topic {
    font-family: Roboto;
    font-size: 15px;
    font-weight: bold;
    letter-spacing: 0.3px;
    color: #000000;
  }
  &--detail {
    padding-left: 10px;
    white-space: pre-wrap;
  }
  &--field {
    margin-top: 20px;
    * {
      font-size: 15px !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/SummaryOfKYC/components/EditKYCForm.vue